import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminSettingsApi } from "@profitlens/api-client";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

export function SitePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["admin", "settings", "site"],
    queryFn: () => adminSettingsApi.getSite(),
  });

  const [siteName, setSiteName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [locale, setLocale] = useState("zh-CN");
  const [currency, setCurrency] = useState("USD");
  const [timezone, setTimezone] = useState("Asia/Shanghai");
  const [trialDays, setTrialDays] = useState("14");
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setSiteName(s.site_name);
    setLogoUrl(s.logo_url ?? "");
    setLocale(s.default_locale);
    setCurrency(s.default_currency);
    setTimezone(s.default_timezone);
    setTrialDays(String(s.trial_days));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      adminSettingsApi.patchSite({
        site_name: siteName,
        logo_url: logoUrl || null,
        default_locale: locale,
        default_currency: currency.toUpperCase(),
        default_timezone: timezone,
        trial_days: Math.max(0, Number.parseInt(trialDays, 10) || 0),
      }),
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("common.saved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings", "site"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) => setFeedback({ kind: "err", text: translateError(err, t) }),
  });

  if (settings.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("settings.siteTitle")}
        subtitle={t("settings.siteSubtitle")}
        actions={
          settings.data?.updated_at ? (
            <p className="text-xs text-muted">
              {t("common.updatedAt")} · {formatDateTime(settings.data.updated_at)}
            </p>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("settings.siteName")}</Label>
              <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.siteLogo")}</Label>
              <Input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://…"
              />
              <p className="text-xs text-muted">{t("settings.siteLogoHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.defaultLocale")}</Label>
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zh-CN">{t("settings.localeZh")}</SelectItem>
                  <SelectItem value="en-US">{t("settings.localeEn")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.defaultCurrency")}</Label>
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                maxLength={3}
                className="w-28 uppercase"
              />
              <p className="text-xs text-muted">{t("settings.defaultCurrencyHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.defaultTimezone")}</Label>
              <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} />
              <p className="text-xs text-muted">{t("settings.defaultTimezoneHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.trialDays")}</Label>
              <Input
                type="number"
                min={0}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="w-28"
              />
              <p className="text-xs text-muted">{t("settings.trialDaysHint")}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 border-t border-border pt-4">
            <Button onClick={() => save.mutate()} disabled={save.isPending || !siteName}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {feedback ? (
              <p
                className={feedback.kind === "ok" ? "text-xs text-success" : "text-xs text-danger"}
              >
                {feedback.text}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
